import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { FloatingContact } from '../components/FloatingContact';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from '../components/ContactFormPopup';
import { Consultation } from '../components/Consultation';
import { ChevronDownIcon, CheckIcon, ArrowRightIcon, BotIcon, BanknoteIcon, GlobeIcon, ZapIcon, TargetIcon, WrenchIcon, MessageCircleIcon, ClipboardListIcon, StarIcon, QuoteIcon, SparklesIcon, RocketIcon, XCircleIcon, CheckCircle2Icon } from 'lucide-react';
// Memoji images
const MEMOJI_FEMALE_1 = 'https://cdn.magicpatterns.com/uploads/etzGm3E7AqZzoRBR6LW1hA/nastyatoki.png';
const MEMOJI_MALE = 'https://cdn.magicpatterns.com/uploads/kywdwn7LuPV87mnksEkraf/yourawesam.png';
const MEMOJI_FEMALE_2 = 'https://cdn.magicpatterns.com/uploads/ivnUpk1HmHTubmYR1EXQ55/ssemenov.png';
export function AiCoursePage() {
  const {
    t,
    language
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      if (diff > 0) {
        setTimeLeft({
          hours: Math.floor(diff / (1000 * 60 * 60) % 24),
          minutes: Math.floor(diff / 1000 / 60 % 60),
          seconds: Math.floor(diff / 1000 % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  // Calculator State
  const [pricePerSite, setPricePerSite] = useState(249);
  const [sitesPerMonth, setSitesPerMonth] = useState(10);
  const monthlyIncome = pricePerSite * sitesPerMonth;
  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const handleConsultationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPopupOpen(true);
  };
  const handleBuyClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsPopupOpen(true);
  };
  // Refs for animations
  const whyRef = useRef(null);
  const isWhyInView = useInView(whyRef, {
    once: true,
    margin: '-100px'
  });
  const faqRef = useRef(null);
  const isFaqInView = useInView(faqRef, {
    once: true,
    margin: '-100px'
  });
  return <div className="min-h-screen w-full" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-1203">
      <Navigation data-id="element-1204" />

      <main data-id="element-1205">
        {/* SECTION 1 — HERO */}
        <section className="relative w-full overflow-hidden px-4 pt-24 md:pt-28 pb-12 md:pb-16 flex flex-col min-h-[calc(100vh-80px)] justify-center" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1206">
          {/* Subtle background accent */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-1207">
            <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px]" style={{
            background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
          }} data-id="element-1208" />
          </div>

          {/* Smooth gradient fade at the bottom to prevent sharp cutoff */}
          <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none z-10" style={{
          background: 'linear-gradient(to top, var(--bg-primary), transparent)'
        }} data-id="element-1209" />

          <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mt-0 md:mt-8 lg:mt-0" data-id="element-1210">
            {/* Left side - Content */}
            <div className="w-full md:flex-1 text-center md:text-left" data-id="element-1211">
              {/* Badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 md:mb-6 border" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)',
              color: 'var(--text-secondary)'
            }} data-id="element-1212">
                <span className="text-xs md:text-sm font-semibold tracking-wide flex items-center gap-1.5" data-id="element-1213">
                  🔥 {t('course.hero.badge')}
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="mb-6 md:mb-8 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1214">
                {t('course.hero.title')}
              </motion.h1>

              {/* ========================================== */}
              {/* DESKTOP ONLY ELEMENTS (Hidden on Mobile)   */}
              {/* ========================================== */}

              {/* Features / Pills - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="hidden md:flex flex-wrap items-center justify-start gap-3 mb-8" data-id="element-1215">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-1216">
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-1217" />
                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1218">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>

              {/* Social Proof - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="hidden md:flex items-center justify-start gap-4 mb-8" data-id="element-1219">
                <div className="flex -space-x-3" data-id="element-1220">
                  <img src={MEMOJI_FEMALE_1} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-blue-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-1221" />
                  <img src={MEMOJI_MALE} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-green-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-1222" />
                  <img src={MEMOJI_FEMALE_2} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-purple-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-1223" />
                </div>
                <div className="flex flex-col items-start" data-id="element-1224">
                  <div className="flex gap-0.5 mb-0.5" data-id="element-1225">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current text-yellow-400" data-id="element-1226" />)}
                  </div>
                  <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1227">
                    <strong style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-1228">
                      {t('course.hero.socialProofCount')}
                    </strong>{' '}
                    {t('course.hero.socialProof')}
                  </span>
                </div>
              </motion.div>

              {/* Pricing & CTA - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="hidden md:flex w-max flex-row items-center gap-8 bg-white rounded-[2rem] p-3 pr-3 pl-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 mt-2" data-id="element-1229">
                <div className="flex flex-col items-start py-0" data-id="element-1230">
                  <span className="text-sm font-semibold mb-1 flex items-center gap-1.5" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1231">
                    🤑 {t('course.hero.specialPrice')}
                  </span>
                  <div className="flex items-baseline gap-3" data-id="element-1232">
                    <span className="text-2xl line-through decoration-2 font-medium" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-1233">
                      $249
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-1234">
                      $99
                    </span>
                  </div>
                </div>

                <button onClick={handleBuyClick} className="bg-[#3B82F6] text-white px-8 py-5 rounded-2xl font-bold text-lg hover:bg-blue-600 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-1235">
                  {t('course.hero.buyCourse')}{' '}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-1236" />
                </button>
              </motion.div>

              {/* ========================================== */}
              {/* MOBILE ONLY ELEMENTS (Hidden on Desktop)   */}
              {/* ========================================== */}

              {/* Mobile Card (Video + Pricing + Social Proof) */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex md:hidden flex-col w-full bg-white rounded-3xl shadow-xl border overflow-hidden mb-6" style={{
              borderColor: 'var(--border)'
            }} data-id="element-1237">
                {/* Video Section */}
                <div className="w-full pt-6 pb-2" data-id="element-1238">
                  <video src="https://cdn.magicpatterns.com/uploads/qvPS7V8fwwawVuSq1Z4F16/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full max-w-[240px] mx-auto h-auto mix-blend-multiply" data-id="element-1239" />
                </div>

                <div className="flex flex-col p-5 sm:p-6 pt-0 gap-6" data-id="element-1240">
                  {/* Pricing Section */}
                  <div className="flex flex-col items-center" data-id="element-1241">
                    <span className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-1242">
                      🤑 {t('course.hero.specialPrice')}
                    </span>
                    <div className="flex items-baseline gap-3 mb-6" data-id="element-1243">
                      <span className="text-2xl line-through decoration-2 font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-1244">
                        $249
                      </span>
                      <span className="text-5xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1245">
                        $99
                      </span>
                    </div>

                    <button onClick={handleBuyClick} className="w-full bg-[#3B82F6] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-600 transition-all shadow-md flex items-center justify-center gap-2 mb-6" data-id="element-1246">
                      {t('course.hero.buyCourse')}{' '}
                      <ArrowRightIcon className="w-5 h-5" data-id="element-1247" />
                    </button>

                    {/* Social Proof - Mobile */}
                    <div className="flex items-center justify-center gap-3 w-full bg-gray-50/50 rounded-2xl p-3 border border-gray-100" data-id="element-1248">
                      <div className="flex -space-x-2.5 flex-shrink-0" data-id="element-1249">
                        <img src={MEMOJI_FEMALE_1} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-blue-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-1250" />
                        <img src={MEMOJI_MALE} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-green-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-1251" />
                        <img src={MEMOJI_FEMALE_2} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-purple-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-1252" />
                      </div>
                      <div className="flex flex-col items-start text-left" data-id="element-1253">
                        <div className="flex gap-0.5 mb-0.5" data-id="element-1254">
                          {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3 h-3 fill-current text-yellow-400" data-id="element-1255" />)}
                        </div>
                        <span className="text-[11px] sm:text-xs font-medium leading-tight" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-1256">
                          <strong style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-1257">
                            {t('course.hero.socialProofCount')}
                          </strong>{' '}
                          {t('course.hero.socialProof')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Pills Section - Mobile (Moved outside) */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="flex md:hidden flex-wrap items-center justify-center gap-2 mb-6" data-id="element-1258">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-1259">
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-1260" />
                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1261">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>
            </div>

            {/* Right side - Video (Desktop only) */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="hidden md:flex md:flex-1 items-center justify-center h-full" data-id="element-1262">
              <div className="relative w-full max-w-[480px] mx-auto rounded-3xl overflow-hidden shadow-2xl border bg-white" style={{
              borderColor: 'var(--border)'
            }} data-id="element-1263">
                <video src="https://cdn.magicpatterns.com/uploads/qvPS7V8fwwawVuSq1Z4F16/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-1264" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — WHY IT'S WORTH IT */}
        <section ref={whyRef} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1265">
          <div className="relative z-10 mx-auto max-w-6xl" data-id="element-1266">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isWhyInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 sm:mb-16 text-center" data-id="element-1267">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1268">
                {t('course.recognize.title')}
              </h2>
            </motion.div>

            {/* Cards Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1269">
              {[{
              emoji: '😤',
              title: t('course.recognize.c1.title'),
              text: t('course.recognize.c1.text')
            }, {
              emoji: '💸',
              title: t('course.recognize.c2.title'),
              text: t('course.recognize.c2.text')
            }, {
              emoji: '😰',
              title: t('course.recognize.c3.title'),
              text: t('course.recognize.c3.text')
            }, {
              emoji: '⏳',
              title: t('course.recognize.c4.title'),
              text: t('course.recognize.c4.text')
            }].map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isWhyInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} data-id="element-1270">
                  <div className="h-full rounded-3xl border p-6 sm:p-8 flex flex-col items-start gap-4" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-1271">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-3xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-1272">
                      {step.emoji}
                    </div>
                    <div data-id="element-1273">
                      <h3 className="mb-2 text-xl font-bold leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-1274">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-1275">
                        {step.text}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 2.5 — WHAT YOU WILL LEARN */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1276">
          <div className="max-w-6xl mx-auto" data-id="element-1277">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-1278">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1279">
                {t('course.offer.title')}
              </h2>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="relative w-full" data-id="element-1280">
              {/* Desktop Unified Background */}
              <div className="hidden lg:block absolute inset-0 rounded-[2.5rem] shadow-xl border pointer-events-none overflow-hidden" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)'
            }} data-id="element-1281">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[100px] opacity-20" style={{
                background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
              }} data-id="element-1282" />
              </div>

              <div className="relative z-10 flex flex-col lg:flex-row gap-6 lg:gap-0 items-stretch lg:p-12 xl:p-16" data-id="element-1283">
                {/* Left Card */}
                <div className="flex-1 flex flex-col justify-center rounded-3xl lg:rounded-none p-6 sm:p-8 lg:p-0 lg:pr-12 xl:pr-16 border lg:border-none shadow-lg lg:shadow-none" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-1284">
                  {/* Tags */}
                  <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-3 mb-6 sm:mb-8" data-id="element-1285">
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-1286">
                      <span className="text-xs sm:text-sm font-medium" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-1287">
                        🖥️ {t('course.offer.tag1')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-1288">
                      <span className="text-xs sm:text-sm font-medium" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-1289">
                        🌱 {t('course.offer.tag2')}
                      </span>
                    </div>
                  </div>

                  {/* Image + Headline */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6" data-id="element-1290">
                    <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8
                  }} className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl overflow-hidden flex-shrink-0 shadow-sm border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-1291">
                      <img src="https://cdn.magicpatterns.com/uploads/4e6J8dUQqhK2kzxt3Mvxwo/IMG_4595.png" alt="AI Website Creator" className="w-full h-full object-cover scale-110" data-id="element-1292" />
                    </motion.div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-1293">
                      {t('course.offer.heading')}
                    </h2>
                  </div>

                  {/* Description */}
                  <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-xl" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1294">
                    {t('course.offer.description')}
                  </p>

                  {/* New Phrase */}
                  <div className="mt-6 sm:mt-8 pt-6 border-t" style={{
                  borderColor: 'var(--border)'
                }} data-id="element-1295">
                    <p className="text-base sm:text-lg font-medium leading-relaxed" data-id="element-1296">
                      <span style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1297">
                        {t('course.offer.tagline1')}{' '}
                      </span>
                      <span style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-1298">
                        {t('course.offer.tagline2')}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Divider (Desktop only) */}
                <div className="hidden lg:block w-px my-4 opacity-50" style={{
                backgroundColor: 'var(--border)'
              }} data-id="element-1299" />

                {/* Right Card: Pricing Integration */}
                <div className="w-full lg:w-[400px] xl:w-[450px] flex flex-col justify-center rounded-3xl lg:rounded-none p-6 sm:p-8 lg:p-0 lg:pl-12 xl:pl-16 border lg:border-none shadow-lg lg:shadow-none" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-1300">
                  <ul className="space-y-4 mb-8 sm:mb-10 flex-1" data-id="element-1301">
                    {[t('course.offer.feature1'), t('course.offer.feature2'), t('course.offer.feature3'), t('course.offer.feature4')].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1302">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-1303">
                          <CheckIcon className="w-3 h-3 text-green-500" data-id="element-1304" />
                        </div>
                        <span className="text-sm sm:text-base font-medium" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1305">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="mb-6" data-id="element-1306">
                    <div className="flex items-baseline gap-2" data-id="element-1307">
                      <span className="text-4xl sm:text-5xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1308">
                        {t('course.offer.price')}
                      </span>
                      <span className="text-sm sm:text-base font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-1309">
                        {t('course.offer.priceLabel')}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-2" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-1310">
                      {t('course.offer.priceDescription')}
                    </p>
                  </div>

                  <button onClick={handleBuyClick} className="block w-full rounded-2xl py-4 text-center text-base sm:text-lg font-bold transition-all hover:scale-[1.02] shadow-lg" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-1311">
                    {t('course.offer.cta')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4 — INCOME CALCULATOR */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1312">
          <div className="max-w-6xl mx-auto" data-id="element-1313">
            <div className="text-center mb-12 sm:mb-16" data-id="element-1314">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1315">
                {t('course.calc.title')}
              </h2>
            </div>

            <div className="rounded-[2.5rem] p-6 sm:p-10 lg:p-12 shadow-xl border" style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border)'
          }} data-id="element-1316">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1317">
                {/* Left: Sliders */}
                <div className="space-y-12" data-id="element-1318">
                  {/* Slider 1 */}
                  <div data-id="element-1319">
                    <div className="flex justify-between items-end mb-6" data-id="element-1320">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1321">
                        {t('course.calc.priceLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-1322">
                        ${pricePerSite}
                      </span>
                    </div>
                    <input type="range" min="149" max="700" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 149) / (700 - 149) * 100}%, var(--border) ${(pricePerSite - 149) / (700 - 149) * 100}%)`
                  }} data-id="element-1323" />
                  </div>

                  {/* Slider 2 */}
                  <div data-id="element-1324">
                    <div className="flex justify-between items-end mb-6" data-id="element-1325">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1326">
                        {t('course.calc.sitesLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-1327">
                        {sitesPerMonth}
                      </span>
                    </div>
                    <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                  }} data-id="element-1328" />
                  </div>
                </div>

                {/* Right: Results */}
                <div className="rounded-3xl p-8 sm:p-10 flex flex-col justify-center relative overflow-hidden" style={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border)'
              }} data-id="element-1329">
                  {/* Background glow */}
                  <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full blur-[80px] opacity-10 pointer-events-none" style={{
                  background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
                }} data-id="element-1330" />

                  <div className="relative z-10" data-id="element-1331">
                    <p className="text-sm font-bold mb-2 uppercase tracking-wider" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-1332">
                      {t('course.calc.potentialIncome')}
                    </p>
                    <div className="flex items-baseline gap-2 mb-8 flex-wrap" data-id="element-1333">
                      <span className="text-5xl sm:text-6xl lg:text-7xl font-bold break-words" style={{
                      color: 'var(--accent)'
                    }} data-id="element-1334">
                        ${monthlyIncome.toLocaleString()}
                      </span>
                      <span className="text-base sm:text-lg font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-1335">
                        {t('course.calc.perMonth')}
                      </span>
                    </div>

                    <div className="space-y-4 mb-10" data-id="element-1336">
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-1337">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-1338">
                          {t('course.calc.coursePrice')}
                        </span>
                        <span className="font-bold" style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-1339">
                          $99
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-1340">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-1341">
                          {t('course.calc.payback')}
                        </span>
                        <span className="font-bold text-green-500" data-id="element-1342">
                          {t('course.calc.paybackValue')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-1343">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-1344">
                          {t('course.calc.netProfit')}
                        </span>
                        <span className="font-bold" style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-1345">
                          ${(monthlyIncome - 99).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={handleBuyClick} className="block w-full rounded-2xl py-4 text-center text-lg font-bold transition-all hover:scale-[1.02] shadow-lg" style={{
                    backgroundColor: 'var(--accent)',
                    color: '#FFFFFF'
                  }} data-id="element-1346">
                      {t('course.offer.cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3.5 — TESTIMONIALS */}
        <section className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1347">
          <div className="relative z-10 mx-auto max-w-7xl" data-id="element-1348">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={true ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 sm:mb-16 text-center" data-id="element-1349">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1350">
                {t('course.testimonials.title')}
              </h2>
              <p className="mx-auto max-w-2xl text-sm sm:text-base" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-1351">
                {t('course.testimonials.subtitle')}
              </p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1352">
              {[{
              name: t('course.testimonials.r1.name'),
              role: t('course.testimonials.r1.role'),
              text: t('course.testimonials.r1.text'),
              image: MEMOJI_FEMALE_1
            }, {
              name: t('course.testimonials.r2.name'),
              role: t('course.testimonials.r2.role'),
              text: t('course.testimonials.r2.text'),
              image: MEMOJI_MALE
            }, {
              name: t('course.testimonials.r3.name'),
              role: t('course.testimonials.r3.role'),
              text: t('course.testimonials.r3.text'),
              image: MEMOJI_FEMALE_2
            }].map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} animate={true ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} data-id="element-1353">
                  <article className="h-full rounded-2xl border p-6 relative flex flex-col" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-1354">
                    <QuoteIcon className="absolute top-4 right-4 w-8 h-8 opacity-10" style={{
                  color: 'var(--accent)'
                }} data-id="element-1355" />

                    <div className="flex items-center gap-3 mb-4" data-id="element-1356">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-[var(--border)] flex-shrink-0" style={{
                    backgroundColor: 'var(--bg-tertiary)'
                  }} data-id="element-1357">
                        <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" data-id="element-1358" />
                      </div>
                      <div data-id="element-1359">
                        <p className="font-semibold text-sm" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-1360">
                          {testimonial.name}
                        </p>
                        <p className="text-xs" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-1361">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-1 mb-4" data-id="element-1362">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" style={{
                    color: '#FBBF24'
                  }} data-id="element-1363" />)}
                    </div>

                    <blockquote className="text-sm sm:text-base leading-relaxed mt-auto" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-1364">
                      "{testimonial.text}"
                    </blockquote>
                  </article>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 3.5 — FEAR VS REALITY */}
        <section className="hidden relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1365">
          <div className="max-w-5xl mx-auto" data-id="element-1366">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-12 sm:mb-16" data-id="element-1367">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl leading-tight" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1368">
                {t('course.fear.title1')} <br className="hidden sm:block" data-id="element-1369" />
                {t('course.fear.title2')}
              </h2>
            </motion.div>

            <div className="flex flex-col gap-4 sm:gap-6" data-id="element-1370">
              {[{
              fear: t('course.fear.f1.fear'),
              reality: t('course.fear.f1.reality')
            }, {
              fear: t('course.fear.f2.fear'),
              reality: t('course.fear.f2.reality')
            }, {
              fear: t('course.fear.f3.fear'),
              reality: t('course.fear.f3.reality')
            }, {
              fear: t('course.fear.f4.fear'),
              reality: t('course.fear.f4.reality')
            }].map((row, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-1371">
                  {/* Fear Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-red-50/50 border-red-100 flex items-center gap-4" data-id="element-1372">
                    <div className="w-10 h-10 rounded-full bg-red-100/50 flex items-center justify-center flex-shrink-0" data-id="element-1373">
                      <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-1374" />
                    </div>
                    <span className="text-base sm:text-lg text-gray-500 line-through decoration-red-300" data-id="element-1375">
                      {row.fear}
                    </span>
                  </div>

                  {/* Reality Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-blue-50/50 border-blue-100 flex items-center gap-4 shadow-sm" data-id="element-1376">
                    <div className="w-10 h-10 rounded-full bg-blue-100/50 flex items-center justify-center flex-shrink-0" data-id="element-1377">
                      <CheckIcon className="w-5 h-5 text-blue-600" data-id="element-1378" />
                    </div>
                    <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-1379">
                      {row.reality}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 8 — FAQ */}
        <section ref={faqRef} className="relative w-full overflow-hidden px-4 py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-1380">
          <div className="relative z-10 mx-auto max-w-3xl" data-id="element-1381">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isFaqInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-1382">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-1383">
                {t('course.faq.title')}
              </h2>
              <p style={{
              color: 'var(--text-secondary)'
            }} data-id="element-1384">
                {t('course.faq.subtitle')}
              </p>
            </motion.div>

            <div className="space-y-4" data-id="element-1385">
              {[{
              q: t('course.faq.q1'),
              a: t('course.faq.a1')
            }, {
              q: t('course.faq.q2'),
              a: t('course.faq.a2')
            }, {
              q: t('course.faq.q3'),
              a: t('course.faq.a3')
            }, {
              q: t('course.faq.q4'),
              a: t('course.faq.a4')
            }, {
              q: t('course.faq.q5'),
              a: t('course.faq.a5')
            }, {
              q: t('course.faq.q6'),
              a: t('course.faq.a6')
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isFaqInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} data-id="element-1386">
                  <div className="rounded-2xl border overflow-hidden" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-1387">
                    <button onClick={() => toggleFaq(index)} className="flex w-full items-center justify-between p-6 text-left transition-colors" aria-expanded={openFaqIndex === index} data-id="element-1388">
                      <h3 className="pr-4 font-semibold text-base" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-1389">
                        {item.q}
                      </h3>
                      <motion.div animate={{
                    rotate: openFaqIndex === index ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className="flex-shrink-0" data-id="element-1390">
                        <ChevronDownIcon className="h-5 w-5" style={{
                      color: 'var(--accent)'
                    }} data-id="element-1391" />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false} data-id="element-1392">
                      {openFaqIndex === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-1393">
                          <div className="border-t px-6 pb-6 pt-4" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-1394">
                            <p className="leading-relaxed" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-1395">
                              {item.a}
                            </p>

                            {/* Duplicate Calculator for Question 2 */}
                            {index === 1 && <div className="mt-8" data-id="element-1396">
                                <div className="rounded-3xl p-6 sm:p-8 border shadow-md" style={{
                          backgroundColor: 'var(--bg-primary)',
                          borderColor: 'var(--border)'
                        }} data-id="element-1397">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1398">
                                    {/* Left: Sliders */}
                                    <div className="space-y-8" data-id="element-1399">
                                      {/* Slider 1 */}
                                      <div data-id="element-1400">
                                        <div className="flex justify-between items-end mb-4" data-id="element-1401">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-1402">
                                            {t('course.calc.priceLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-1403">
                                            ${pricePerSite}
                                          </span>
                                        </div>
                                        <input type="range" min="149" max="700" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 149) / (700 - 149) * 100}%, var(--border) ${(pricePerSite - 149) / (700 - 149) * 100}%)`
                                }} data-id="element-1404" />
                                      </div>

                                      {/* Slider 2 */}
                                      <div data-id="element-1405">
                                        <div className="flex justify-between items-end mb-4" data-id="element-1406">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-1407">
                                            {t('course.calc.sitesLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-1408">
                                            {sitesPerMonth}
                                          </span>
                                        </div>
                                        <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                                }} data-id="element-1409" />
                                      </div>
                                    </div>

                                    {/* Right: Results */}
                                    <div className="rounded-2xl p-6 flex flex-col justify-center relative overflow-hidden" style={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border)'
                            }} data-id="element-1410">
                                      <div className="relative z-10" data-id="element-1411">
                                        <p className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                                  color: 'var(--text-secondary)'
                                }} data-id="element-1412">
                                          {t('course.calc.potentialIncome')}
                                        </p>
                                        <div className="flex items-baseline gap-2 mb-6 flex-wrap" data-id="element-1413">
                                          <span className="text-4xl sm:text-5xl font-bold break-words" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-1414">
                                            ${monthlyIncome.toLocaleString()}
                                          </span>
                                          <span className="text-sm font-medium" style={{
                                    color: 'var(--text-muted)'
                                  }} data-id="element-1415">
                                            {t('course.calc.perMonth')}
                                          </span>
                                        </div>

                                        <div className="space-y-3 mb-6" data-id="element-1416">
                                          <div className="flex justify-between items-center py-2 border-b" style={{
                                    borderColor: 'var(--border)'
                                  }} data-id="element-1417">
                                            <span className="text-sm font-medium" style={{
                                      color: 'var(--text-secondary)'
                                    }} data-id="element-1418">
                                              {t('course.calc.coursePrice')}
                                            </span>
                                            <span className="text-sm font-bold" style={{
                                      color: 'var(--text-primary)'
                                    }} data-id="element-1419">
                                              $99
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-2 border-b" style={{
                                    borderColor: 'var(--border)'
                                  }} data-id="element-1420">
                                            <span className="text-sm font-medium" style={{
                                      color: 'var(--text-secondary)'
                                    }} data-id="element-1421">
                                              {t('course.calc.payback')}
                                            </span>
                                            <span className="text-sm font-bold text-green-500" data-id="element-1422">
                                              {t('course.calc.paybackValue')}
                                            </span>
                                          </div>
                                        </div>

                                        <button onClick={handleBuyClick} className="block w-full rounded-xl py-3 text-center text-sm font-bold transition-all hover:scale-[1.02] shadow-md" style={{
                                  backgroundColor: 'var(--accent)',
                                  color: '#FFFFFF'
                                }} data-id="element-1423">
                                          {t('course.offer.cta')}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 9 — FINAL CTA (Consultation Component) */}
        <Consultation data-id="element-1424" />
      </main>

      <Footer data-id="element-1425" />
      <CookieBanner data-id="element-1426" />
      <FloatingContact data-id="element-1427" />

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-1428" />
    </div>;
}